/**
 * The brief's add (session W4): one items[] POST to /cart/add.js, the drawer's
 * CartLinesUpdateEvent with its promise, and what follows a success. Split
 * from wc-brief.js, which theme check limits to 60 KB as a first-load script;
 * each function takes the <wc-brief> element.
 */

/**
 * What the upload app holds. Its fields are recognised by WHERE they are —
 * inside this block's upload slot — never by name, because the app chooses
 * its own names. That same rule is the allow-list at add time: a field a
 * ScriptTag app injects anywhere else in the form never reaches the cart.
 */
export function uploadState(el) {
  const slot = el.querySelector("[data-wc-uploads]");
  const fields = slot ? [...slot.querySelectorAll('[name^="properties["]')] : [];
  /** @type {[string, string][]} */
  const entries = [];
  for (const field of fields) {
    const input = /** @type {HTMLInputElement} */ (field);
    if (input.disabled || ((input.type === "checkbox" || input.type === "radio") && !input.checked)) continue;
    const key = input.name.slice("properties[".length, -1);
    if (key) entries.push([key, input.value]);
  }
  const count = entries.filter(([k, v]) => !k.startsWith("_") && v.trim()).length;
  return { detectable: fields.length > 0, count, entries };
}

export async function add(el) {
  if (el.adding) return;
  // Answered AT ONCE (K8-04): the button says "Adding…" before anything is
  // awaited, and a second tap cannot start a second add while a variant is
  // still resolving. Every way out of here clears it (the finally below).
  el.adding = true;
  el.toggleAttribute("data-adding", true);
  el.setBusy(false);
  try {
    await send(el);
  } finally {
    el.adding = false;
    el.toggleAttribute("data-adding", false);
    el.setBusy(Boolean(el.pending));
  }
}

/** The add itself, once the button has answered the tap. @param {any} el */
async function send(el) {
  if (el.pending) await el.pending.catch(() => {});
  const core = el.core;
  const state = el.readState();
  const upload = uploadState(el);
  const invalid = core.firstInvalidStep(state, el.data, {
    uploadsDetectable: upload.detectable,
    uploadCount: upload.count,
  });
  if (invalid) {
    if (invalid.step !== el.step) el.go(invalid.step);
    // Wait for the step to be on screen before pointing at the field.
    requestAnimationFrame(() => el.showErrors(invalid.step, invalid.errors));
    return;
  }

  const items = core.buildItems(state, el.data, upload.entries);
  const sectionIds = [...document.querySelectorAll("cart-items-component[data-section-id]")].map(
    (node) => /** @type {HTMLElement} */ (node).dataset.sectionId,
  );
  const body = {
    items,
    ...(sectionIds.length ? { sections: sectionIds.join(","), sections_url: location.pathname } : {}),
  };

  const Events = el.events;
  const deferred = Events?.CartLinesUpdateEvent?.createPromise?.();
  if (deferred) {
    el.dispatchEvent(
      new Events.CartLinesUpdateEvent({
        action: "add",
        context: "product",
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      }),
    );
  }

  try {
    const response = await fetch(el.dataset.addUrl || "/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({ status: response.status, description: response.statusText }));
    // /cart.js starts now and is awaited only where its cart is needed. Waiting
    // for it before resolving held the drawer shut for its whole round trip
    // (K8-04: the drawer opened ~1.2 s after add.js answered).
    const cartRequest = fetch(`${el.dataset.cartUrl || "/cart"}.js`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .catch(() => null);

    if (!response.ok || result.status) {
      // A refused add waits for the cart: Horizon's cart icon renders its
      // count from this event even on an error.
      const cart = await cartRequest;
      // /cart/add.js is all-or-nothing: one bad line and nothing is added.
      // The drawer is told it errored so it stays shut and does not morph.
      deferred?.resolve({
        cart: cart && Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart?.items, source: "wc-brief", sourceId: el.id },
      });
      dispatchCartError(el, result);
      showAddError(el, result);
      return;
    }

    // Resolved NOW, from the add's own sections: Horizon's drawer opens on this
    // resolve and morphs from `sections` without a request, so it no longer
    // waits for /cart.js (measured, brief-harness/addtiming.mjs: add.js answer
    // to drawer open 1,037 ms -> 12 ms with /cart.js at 1 s). The cart icon
    // falls back to `itemCount` when `cart` is null, so it gets the ABSOLUTE
    // count it would have shown: its own count before, plus what was added.
    const added = items.reduce((n, i) => n + i.quantity, 0);
    const before = /** @type {any} */ (document.querySelector("cart-icon"))?.currentCartCount;
    deferred?.resolve({
      cart: null,
      detail: {
        didError: false,
        items: result.items,
        sections: result.sections,
        source: "wc-brief",
        sourceId: el.id,
        itemCount: Number.isFinite(before) ? before + added : added,
      },
    });
    onAdded(el, state);
    // The cart object follows, for the count and the header's announcement,
    // in one update carrying the SAME sections: without them the cart items
    // component refetches its section. "update", so the open drawer is left
    // as it is; after the first morph has settled.
    cartRequest.then(async (cart) => {
      if (!cart || !Events?.CartLinesUpdateEvent) return;
      await new Promise((settled) => setTimeout(settled, 400));
      const follow = Events.CartLinesUpdateEvent.createPromise();
      el.dispatchEvent(
        new Events.CartLinesUpdateEvent({ action: "update", context: "product", lines: [], promise: follow.promise }),
      );
      follow.resolve({
        cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, sections: result.sections, source: "wc-brief", sourceId: el.id },
      });
    });
  } catch (error) {
    deferred?.reject?.(error);
    showAddError(el, { description: el.message("network") });
  }
}

/** @param {any} el @param {any} result */
function dispatchCartError(el, result) {
  const Events = el.events;
  if (!Events?.CartErrorEvent) return;
  el.dispatchEvent(
    new Events.CartErrorEvent({
      error: result?.message || "Add to cart failed",
      code: "INVALID",
      detail: { description: result?.description },
    }),
  );
}

/** @param {any} el @param {any} result */
function showAddError(el, result) {
  // `:scope`: the brief itself carries data-step="review" here, and without it
  // the add's error went into step 1's hidden box, where nobody saw it (K8-02).
  const box = /** @type {HTMLElement | null} */ (el.querySelector(':scope [data-step="review"] [data-wc-error]'));
  if (!box) return;
  // Shopify's own description is written for shoppers ("You can only add 0 of
  // this item to your cart"); anything else gets our plain sentence.
  box.textContent =
    typeof result?.description === "string" && result.description ? result.description : el.message("add_failed");
  box.hidden = false;
  box.focus();
}

/** @param {any} el @param {any} state */
function onAdded(el, state) {
  el.clearSave();
  el.track("added");
  el.announce(el.message("added"));
  el.dispatchEvent(new CustomEvent("wc-brief:added", { bubbles: true, detail: { briefId: state.briefId } }));
  // A second portrait starts a new brief; the answers stay for convenience.
  el.briefId = el.core.newBriefId();
  el.tracked.clear();
  el.engaged = false;
  // On a phone the drawer opens over the page, not over a full-screen brief.
  if (el.hasAttribute("data-focus")) el.close();
}
